import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Swal, { SweetAlertOptions, SweetAlertResult } from "sweetalert2";

@Injectable()
export class SwalModalService {
    isActivated = true;

    constructor(private translate: TranslateService) {}

    /**
     * Display a modal error
     *
     * @param text
     * @returns
     */
    error(text): Promise<SweetAlertResult> {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire({
            title: this.translate.instant("modal.error"),
            html: text,
            icon: "error",
            showCancelButton: false,
            confirmButtonColor: "primary",
        });
    }

    /**
     * Display a success modal
     * @param title
     * @param timer
     * @returns
     */
    success(title, timer = 1500, showConfirmButton: boolean = false): Promise<SweetAlertResult> {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire({
            icon: "success",
            title: title,
            showConfirmButton: showConfirmButton,
            confirmButtonColor: "primary",
            timer: timer,
        });
    }

    /**
     * Open a modal usiong options
     *
     * @param sweetAlertOptions
     * @returns
     */
    open(sweetAlertOptions: SweetAlertOptions): Promise<SweetAlertResult> {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire(sweetAlertOptions);
    }

    /**
     * Open a unsaved confirmation modal
     *
     * @returns
     */
    unsavedConfirm(): Promise<SweetAlertResult> {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire({
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "primary",
            title: this.translate.instant("modal.unsaved.title"),
            text: this.translate.instant("modal.unsaved.text"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonText: this.translate.instant("modal.yes"),
        });
    }

    /**
     * Open a confirm modal
     *
     * @param sweetAlertOptions
     * @returns
     */
    confirm(sweetAlertOptions: SweetAlertOptions) {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire(
            Object.assign(
                {
                    title: this.translate.instant("modal.confirm"),
                    text: this.translate.instant("modal.sure"),
                    icon: "warning",
                    showCloseButton: true,
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonColor: "primary",
                    confirmButtonText: this.translate.instant("modal.yes"),
                    cancelButtonText: this.translate.instant("modal.no"),
                },
                sweetAlertOptions
            )
        );
    }
    confirmText(sweetAlertOptions: SweetAlertOptions) {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire(
            Object.assign(
                {
                    title: this.translate.instant("modal.confirm"),
                    text: this.translate.instant("modal.sure"),
                    input: "text",
                    inputPlaceholder: "Note",
                    icon: "warning",
                    showCloseButton: true,
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonColor: "primary",
                    confirmButtonText: this.translate.instant("modal.yes"),
                    cancelButtonText: this.translate.instant("modal.no"),
                },
                sweetAlertOptions
            )
        );
    }

    /**
     * Open a confirm modal for a deletion
     * @param content
     * @returns
     */
    delete(content: boolean = false) {
        if (!this.isActivated) {
            return;
        }
        return this.confirm({
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            title: this.translate.instant("modal.delete"),
            html: content ? this.translate.instant("modal.elementType") : this.translate.instant("modal.revert"),
            confirmButtonText: this.translate.instant("modal.yes"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonColor: "primary",
        });
    }

    deleteWithContent(content: string) {
        if (!this.isActivated) {
            return;
        }
        return this.confirm({
            showCancelButton: true,
            focusConfirm: false,
            title: this.translate.instant("modal.delete"),
            text: this.translate.instant(content),
            confirmButtonText: this.translate.instant("modal.yes"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonColor: "primary",
        });
    }

    /**
     * Open a modal to ask confirmation before deleting a section (pim)
     * @returns
     */
    deleteSection() {
        if (!this.isActivated) {
            return;
        }

        return this.confirm({
            showCancelButton: true,
            title: this.translate.instant("modal.delete"),
            text: this.translate.instant("modal.deleteEt"),
            confirmButtonText: this.translate.instant("modal.yes"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonColor: "primary",
        });
    }

    /**
     * Open a modal to ask confirmation before deleting a group page (project)
     * @returns
     */
    deleteGroupPage(name: string, pageLength: number) {
        if (!this.isActivated) {
            return;
        }
        return this.confirm({
            showCancelButton: true,
            title: this.translate.instant("modal.delete"),
            text: this.translate.instant("modal.groupPage") + name + ` (${pageLength} ${pageLength > 1 ? this.translate.instant("modal.pages") : this.translate.instant("modal.page")})`,
            confirmButtonText: this.translate.instant("modal.yes"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonColor: "primary",
        });
    }

    /**
     * Show an info modal when a project's configuration is not completed
     * @param content
     * @returns
     */
    info(content) {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire({
            icon: "info",
            title: this.translate.instant("modal.info"),
            html: content,
            confirmButtonText: this.translate.instant("modal.ok"),
            confirmButtonColor: "primary",
        } as SweetAlertOptions);
    }

    updatePage() {
        if (!this.isActivated) {
            return;
        }
        return this.confirm({
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            title: this.translate.instant("projects.flatplan.updatePage"),
            text: this.translate.instant("modal.updatePage"),
            confirmButtonText: this.translate.instant("modal.yes"),
            cancelButtonText: this.translate.instant("modal.no"),
            confirmButtonColor: "primary",
        });
    }

    historyModalWorkflow(html) {
        if (!this.isActivated) {
            return;
        }
        return Swal.fire({
            icon: "info",
            html: html,
        });
    }

    async import(importType: string, importInfos: any, importData: string) {
        const t = (key: string) => this.translate.instant(key);

        let infos = "";
        importInfos.forEach((info) => (infos += `<li>${info}</li>`));

        const note = "product" == importData ? t("import.columnInfos.fieldset") : "";

        const dropIcon = `
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M14 3v5h5" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M12 17v-5m0 0-2 2m2-2 2 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;

        const noteBlock = note
            ? `<h6 class="import-info-title">${t("import.toNote")}</h6>
               <p class="import-info-note">${note}</p>`
            : "";

        const html = `
            <div class="import-modal-body">
                <label class="import-dropzone" id="importDropzone">
                    <input type="file" id="importFileInput" class="import-file-input" accept=".csv, .xls, .xlsx" aria-label="${t("import.uploadFile")}">
                    <span class="import-dropzone-icon">${dropIcon}</span>
                    <span class="import-dropzone-title">${t("import.dropTitle")}</span>
                    <span class="import-dropzone-or">${t("import.or")}</span>
                    <span class="btn btn-primary import-select-btn"><i class="pi pi-folder-open mr-2"></i>${t("general.choose.file")}</span>
                    <span class="import-file-name" id="importFileName">${t("import.noFile")}</span>
                    <span class="import-dropzone-meta">${t("import.acceptedFormats")}<br>${t("import.maxSize")}</span>
                </label>
                <div class="import-info">
                    <h6 class="import-info-title">${t("import.requiredInfos")}</h6>
                    <ul class="import-info-list">${infos}</ul>
                    ${noteBlock}
                    <a class="btn btn-outline-primary import-download" target="_self" href="../../../../../assets/${importType}" download="${importType}">
                        <i class="pi pi-download mr-2"></i>${t(`import.download.${importData}`)}
                    </a>
                </div>
            </div>
        `;

        const { value: file }: any = await Swal.fire({
            title: t(`import.${importData}`),
            html,
            width: "900px",
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: t("general.cancel"),
            confirmButtonText: t("import.continue"),
            focusConfirm: false,
            customClass: { popup: "import-modal-popup" },
            didOpen: () => {
                const input = document.getElementById("importFileInput") as HTMLInputElement;
                const nameEl = document.getElementById("importFileName");
                const dropzone = document.getElementById("importDropzone");

                input.addEventListener("change", () => {
                    nameEl.textContent = input.files && input.files.length ? input.files[0].name : t("import.noFile");
                });

                ["dragenter", "dragover"].forEach((ev) =>
                    dropzone.addEventListener(ev, (e) => {
                        e.preventDefault();
                        dropzone.classList.add("is-dragover");
                    })
                );
                ["dragleave", "drop"].forEach((ev) =>
                    dropzone.addEventListener(ev, (e) => {
                        e.preventDefault();
                        dropzone.classList.remove("is-dragover");
                    })
                );
                dropzone.addEventListener("drop", (e: DragEvent) => {
                    if (e.dataTransfer && e.dataTransfer.files.length) {
                        input.files = e.dataTransfer.files;
                        nameEl.textContent = input.files[0].name;
                    }
                });
            },
            preConfirm: () => {
                const input = document.getElementById("importFileInput") as HTMLInputElement;
                if (!input.files || !input.files.length) {
                    Swal.showValidationMessage(t("import.noFileSelected"));
                    return false;
                }
                return input.files[0];
            },
        });

        let data = null;

        const acceptedType = ["text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

        if (file) {
            if (acceptedType.includes(file.type)) {
                data = file;
            } else {
                this.error(this.translate.instant("import.notSupportedFile", { expected: "text/csv or application/vnd.ms-excel", received: file.type }));
            }
        }

        return data;
    }
}
